/* eslint-disable @typescript-eslint/naming-convention -- Custom API parameters and replies, and PouchDB's include_docs, are named by those APIs */
import {
  DeltaService,
  EntityNames,
  EntitySyncInfo,
} from '../delta/delta.service';
import { AuthenticationService } from '../../services/authentication.service';
import { DeviceService } from '../../services/device/device.service';
import { DiskService } from '../../services/disk/disk.service';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { Injectable } from '@angular/core';
import { PREFIX_SEARCH_ENDKEY_UNICODE } from '../../config/pouch-db.config';
import type { ZodType } from 'zod';

/** What every `indskr_Get…Bundle` answer carries: the payload, as a JSON string, and its hash. */
export interface BundleActionResponse {
  Bundle?: string;
  BundleVersion?: string;
  Changed?: boolean;
}

/** A bundle is committed only whole: `status: 'ok'` and `complete: true`, as the server promises. */
export interface CommittableBundle {
  status?: string;
  complete?: boolean;
}

/** What one bundle sync needs to say; the rest is the same for every bundle. */
export interface BundleSyncRequest<
  TBundle extends CommittableBundle,
  TDoc extends { bundleVersion?: string },
> {
  action: string;
  entityName: EntityNames;
  /** The PouchDB document the projection lives in. */
  key: string;
  /** Stored beside the bundle, e.g. the position ids or screen key the projection is for. */
  stored?: Partial<TDoc>;
  /** An answer with no payload and `Changed: false` means "keep yours" for this action. */
  keepOnSuppressed?: boolean;
  /** The payload's shape; a bundle that does not match is not cached. */
  schema?: ZodType;
  /** The request's `RawData`, built from what the device already holds. */
  rawData(cached: TDoc | null): Record<string, unknown>;
  /** Throws when a committable bundle is still not safe to cache. */
  validate?(bundle: TBundle): void;
  /** Adds what the device needs beside the payload, before the one write. A throw fails the sync. */
  enrich?(bundle: TBundle, cached: TDoc | null): Promise<TBundle>;
  /** Records the bundle carries, for the sync report. */
  count?(bundle: TBundle): number;
  /** Runs after the bundle was written, e.g. to log what it removed. */
  afterWrite?(bundle: TBundle): void;
}

/**
 * The full-snapshot bundle sync shared by pricing, delivery schedules and screen configuration:
 * the server returns everything on every call and absence means deleted, so a projection is
 * replaced in one atomic write, and only ever by a complete bundle. `BundleVersion` is sent for
 * diagnostics; `Changed` never skips the write, because its hash misses assignment changes.
 */
@Injectable({ providedIn: 'root' })
export class BundleSyncService {
  constructor(
    private readonly dynamics: DynamicsClientService,
    private readonly disk: DiskService,
    private readonly deviceService: DeviceService,
    private readonly deltaService: DeltaService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  public get isOffline(): boolean {
    return this.deviceService.isOffline;
  }

  public positionIds(): string[] {
    return (this.authenticationService.user?.positions ?? [])
      .map((position) => position.ID)
      .filter(Boolean);
  }

  public businessUnitId(): string | null {
    return this.authenticationService.user?.xBusinessUnitId || null;
  }

  /**
   * The `subjects` sent in RawData, keyed by entity logical name. Business unit and system user
   * go too: a rule the server adds later can filter on either without a client release, and a
   * subject no query binds is ignored.
   */
  public subjects(
    positionIds: string[],
    extra: Record<string, string[]> = {},
  ): Record<string, string[]> {
    const user = this.authenticationService.user;
    const subjects: Record<string, string[]> = {
      position: positionIds,
      ...extra,
    };
    if (user?.xBusinessUnitId) subjects.businessunit = [user.xBusinessUnitId];
    if (user?.xSystemUserID) subjects.systemuser = [user.xSystemUserID];
    return subjects;
  }

  public async cached<TDoc>(key: string): Promise<TDoc | null> {
    return ((await this.disk.retrieve(key, true)) as TDoc | null) ?? null;
  }

  /** Syncs one projection; true when the device copy was replaced. Never throws. */
  public async sync<
    TBundle extends CommittableBundle,
    TDoc extends { bundleVersion?: string },
  >(request: BundleSyncRequest<TBundle, TDoc>): Promise<boolean> {
    const cached = await this.cached<TDoc>(request.key);
    const syncInfo: EntitySyncInfo = {
      entityName: request.entityName,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true,
    };
    let replaced = false;
    try {
      const response = (await this.dynamics.executeUnboundAction(
        request.action,
        {
          RawData: JSON.stringify(request.rawData(cached)),
          BundleVersion: cached?.bundleVersion ?? '',
        },
      )) as BundleActionResponse | null;
      const suppressed =
        request.keepOnSuppressed &&
        !response?.Bundle &&
        response?.Changed === false &&
        !!cached;
      if (!suppressed) {
        if (!response?.Bundle)
          throw new Error(`${request.action}: no payload for ${request.key}`);
        const bundle = JSON.parse(response.Bundle) as TBundle;
        assertCommittable(request, bundle);
        const stored = request.enrich
          ? await request.enrich(bundle, cached)
          : bundle;
        const written = await this.disk.updateOrInsert(request.key, () => ({
          ...request.stored,
          _id: request.key,
          bundleVersion: response.BundleVersion,
          fetchedAt: new Date().toISOString(),
          bundle: stored,
        }));
        // The disk reports a failed write by answering nothing: the old copy stays and is not purged.
        if (!written)
          throw new Error(`${request.action}: ${request.key} was not written`);
        syncInfo.totalSynced = request.count?.(stored) ?? 1;
        replaced = true;
        request.afterWrite?.(stored);
      }
    } catch (error) {
      syncInfo.syncStatus = false;
      syncInfo.totalFailed++;
      this.deltaService.addSyncErrorToEntitySyncInfo(
        syncInfo,
        request.action,
        error,
      );
      console.error(
        `BundleSyncService: ${request.action} failed for ${request.key}`,
        error,
      );
    }
    this.deltaService.addEntitySyncInfo(syncInfo);
    return replaced;
  }

  /** Removes every document under `prefix` but `keep`. Housekeeping: it never fails a sync. */
  public async purge(prefix: string, keep: ReadonlySet<string>): Promise<void> {
    try {
      const all = (await this.disk.batchFetch(
        {
          include_docs: false,
          startkey: prefix,
          endkey: `${prefix}${PREFIX_SEARCH_ENDKEY_UNICODE}`,
        },
        false,
      )) as { rows?: { id?: string }[] } | null;
      for (const id of (all?.rows ?? [])
        .map((row) => row.id)
        .filter((key): key is string => !!key)) {
        if (!keep.has(id)) await this.disk.remove(id);
      }
    } catch (error) {
      console.warn(`BundleSyncService: purge of ${prefix} failed`, error);
    }
  }
}

/** Throws unless the bundle may replace the device copy. */
function assertCommittable<
  TBundle extends CommittableBundle,
  TDoc extends { bundleVersion?: string },
>(request: BundleSyncRequest<TBundle, TDoc>, bundle: TBundle): void {
  // A short read must never be committed: what it leaves out would read as deleted.
  if (bundle?.status !== 'ok' || bundle?.complete !== true) {
    throw new Error(
      `${request.action}: incomplete bundle for ${request.key} (status=${bundle?.status}, complete=${bundle?.complete})`,
    );
  }
  const shape = request.schema?.safeParse(bundle);
  if (shape && !shape.success)
    throw new Error(
      `${request.action}: unexpected bundle for ${request.key}: ${describeIssues(shape.error.issues)}`,
    );
  request.validate?.(bundle);
}

/** The first few mismatches, where they are: enough to find the column without logging the payload. */
function describeIssues(
  issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>,
): string {
  const shown = issues
    .slice(0, 3)
    .map((issue) => `${issue.path.map(String).join('.')}: ${issue.message}`);
  const more =
    issues.length > shown.length
      ? ` (+${issues.length - shown.length} more)`
      : '';
  return shown.join('; ') + more;
}
